import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, MapPin, Loader2, Building2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MisVentas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ventas, setVentas] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const authRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!authRes.ok) { navigate('/login'); return; }
        const res = await fetch(`${BACKEND_URL}/api/comisionista/mis-ventas`, { credentials: 'include' });
        if (res.ok) setVentas(await res.json());
        else toast.error('Error al cargar ventas');
      } catch { toast.error('Error de conexión'); }
      finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  const totalComision = ventas.reduce((s, v) => s + (v.comision_monto || 0), 0);

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>;
  }

  return (
    <div data-testid="mis-ventas-page" className="p-4 sm:p-6 space-y-5 max-w-[1100px] mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <ShoppingBag className="w-6 h-6 text-[#C6A54A]" />
          Mis Ventas
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Lotes vendidos y comisiones generadas</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-emerald-500/10 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Ventas</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{ventas.length}</p>
        </div>
        <div className="bg-[#C6A54A]/10 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Comisiones Totales</p>
          <p className="text-2xl font-bold text-[#C6A54A] mt-1">${totalComision.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Pendiente</p>
          <p className="text-2xl font-bold text-zinc-300 mt-1">
            ${ventas.reduce((s, v) => s + (v.comision_pendiente || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {ventas.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <ShoppingBag className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin ventas registradas</h3>
          <p className="text-sm text-zinc-500">Las ventas aparecen cuando un administrador confirma la venta de un lote que apartaste</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="hidden sm:grid grid-cols-7 gap-2 px-4 py-2 text-xs text-zinc-500 uppercase tracking-wider">
            <span>Lote</span>
            <span>Cliente</span>
            <span>Fraccionamiento</span>
            <span className="text-right">Precio</span>
            <span className="text-right">%</span>
            <span className="text-right">Comision</span>
            <span className="text-right">Pendiente</span>
          </div>
          {ventas.map(v => (
            <div key={v.lote_id} data-testid={`venta-${v.lote_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
              {/* Mobile layout */}
              <div className="sm:hidden space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-bold text-white">Lote #{v.numero}</span>
                    <span className="text-xs text-zinc-500 ml-2">Mz {v.manzana}</span>
                  </div>
                  <span className="text-[#C6A54A] font-bold">${v.comision_monto?.toLocaleString('es-MX')}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{v.cliente_nombre || '-'}</span>
                  <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{v.fraccionamiento_nombre}</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{v.moneda} ${v.precio?.toLocaleString('es-MX')}</span>
                  <span>({v.porcentaje_comision}%)</span>
                </div>
                {v.comision_pendiente > 0 && (
                  <p className="text-xs text-amber-400">Pendiente: ${v.comision_pendiente?.toLocaleString('es-MX')}</p>
                )}
              </div>
              {/* Desktop layout */}
              <div className="hidden sm:grid grid-cols-7 gap-2 items-center">
                <span className="font-semibold text-white">#{v.numero} <span className="text-zinc-500 text-xs">Mz {v.manzana}</span></span>
                <span className="text-sm text-zinc-300 truncate">{v.cliente_nombre || '-'}</span>
                <span className="text-sm text-zinc-400 truncate">{v.fraccionamiento_nombre}</span>
                <span className="text-sm text-zinc-300 text-right">${v.precio?.toLocaleString('es-MX')}</span>
                <span className="text-sm text-[#C6A54A] text-right">{v.porcentaje_comision}%</span>
                <span className="text-sm font-semibold text-emerald-400 text-right">${v.comision_monto?.toLocaleString('es-MX')}</span>
                <span className={`text-sm text-right font-medium ${v.comision_pendiente > 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                  ${v.comision_pendiente?.toLocaleString('es-MX')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
